import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/useSettings";
import { CartProvider } from "@/lib/useCart";
import { CustomerAuthProvider } from "@/lib/useCustomerAuth";

import PublicLayout from "@/components/public/PublicLayout";
import HomePage from "@/pages/public/HomePage";
import { ServicesListPage, ServiceDetailPage } from "@/pages/public/ServicesPage";
import { RealisationsListPage, RealisationDetailPage } from "@/pages/public/RealisationsPage";
import { BlogListPage, BlogDetailPage } from "@/pages/public/BlogPage";
import ZonePage from "@/pages/public/ZonePage";
import { ContactPage, DevisPage, LegalPage, DeliveryReturnsPage } from "@/pages/public/MiscPages";
import LocalPage from "@/pages/public/LocalPage";

import LoginPage from "@/pages/admin/LoginPage";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ServicesAdmin from "@/pages/admin/ServicesAdmin";
import RealisationsAdmin from "@/pages/admin/RealisationsAdmin";
import BlogAdmin from "@/pages/admin/BlogAdmin";
import { FAQAdmin, ZonesAdmin } from "@/pages/admin/SimpleAdminPages";
import LeadsAdmin from "@/pages/admin/LeadsAdmin";
import { RankTrackerAdmin, AnalyticsAdmin, LocalSEOAdmin } from "@/pages/admin/AdvancedAdminPages";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import MediaAdmin from "@/pages/admin/MediaAdmin";
import ShortLinksAdmin from "@/pages/admin/ShortLinksAdmin";
import ThemesAdmin from "@/pages/admin/ThemesAdmin";
import ProductsAdmin from "@/pages/admin/ProductsAdmin";
import PickupAdmin from "@/pages/admin/PickupAdmin";
import OrdersAdmin from "@/pages/admin/OrdersAdmin";
import HeaderAdmin from "@/pages/admin/HeaderAdmin";
import QrLabelsAdmin from "@/pages/admin/QrLabelsAdmin";
import ShopDashboard from "@/pages/admin/ShopDashboard";
import TeamAdmin from "@/pages/admin/TeamAdmin";
import BookingsAdmin from "@/pages/admin/BookingsAdmin";
import BookingsConfigAdmin from "@/pages/admin/BookingsConfigAdmin";
import WidgetsConsoleAdmin from "@/pages/admin/WidgetsConsoleAdmin";
import TechnicalAdmin from "@/pages/admin/TechnicalAdmin";
import LegalConfigAdmin from "@/pages/admin/LegalConfigAdmin";
import LegalDocsAdmin from "@/pages/admin/LegalDocsAdmin";
import BillingAdmin from "@/pages/admin/BillingAdmin";
import EmailsAdmin from "@/pages/admin/EmailsAdmin";
import ShippingAdmin from "@/pages/admin/ShippingAdmin";
import BusinessRulesAdmin from "@/pages/admin/BusinessRulesAdmin";
import PopupCallbackAdmin from "@/pages/admin/PopupCallbackAdmin";
import WishlistAdmin from "@/pages/admin/WishlistAdmin";
import PaymentsAdmin from "@/pages/admin/PaymentsAdmin";
import OnboardingPage from "@/pages/admin/OnboardingPage";
import CockpitAdmin from "@/pages/admin/CockpitAdmin";
import StudioAdmin from "@/pages/admin/StudioAdmin";
import HomeSectionsToggleAdmin from "@/pages/admin/HomeSectionsToggleAdmin";
import AgencyPairingPage from "@/pages/admin/AgencyPairingPage";
import ProductsImporterAdmin from "@/pages/admin/ProductsImporterAdmin";
import TeamPage from "@/pages/public/TeamPage";
import { WorkshopsListPage, WorkshopDetailPage } from "@/pages/jardins-ole/WorkshopsPage";
import WorkshopsAdmin from "@/pages/jardins-ole/WorkshopsAdmin";
import PodSsoConsumePage from "@/pages/admin/PodSsoConsumePage";
import AgentBridgeAdmin from "@/pages/admin/AgentBridgeAdmin";
import ShopPage from "@/pages/public/ShopPage";
import ProductPage from "@/pages/public/ProductPage";
import ReservationPage from "@/pages/public/ReservationPage";
import BookingConfirmationPage from "@/pages/public/BookingConfirmationPage";
import { RentalsListPage, RentalDetailPage, RentalConfirmationPage } from "@/pages/public/RentalsPage";
import RentalsAdmin from "@/pages/admin/RentalsAdmin";
import RentalsCalendarAdmin from "@/pages/admin/RentalsCalendarAdmin";
import RentalsDashboardAdmin from "@/pages/admin/RentalsDashboardAdmin";
import SectorPresetsAdmin from "@/pages/admin/SectorPresetsAdmin";
import AgencySitesDashboard from "@/pages/admin/AgencySitesDashboard";
import AgencyAlertsPage from "@/pages/admin/AgencyAlertsPage";
import AgencyLayout from "@/pages/agency/AgencyLayout";
import AgencyLoginPage from "@/pages/agency/AgencyLoginPage";
import AgencySettingsPage from "@/pages/agency/AgencySettingsPage";
import ThemeGeneratorAdvanced from "@/pages/agency/ThemeGeneratorAdvanced";
import ContentScraperPage from "@/pages/agency/ContentScraperPage";
import OnboardingDraftsPage from "@/pages/agency/OnboardingDraftsPage";
import AgencyCockpitAdmin from "@/pages/agency/AgencyCockpitAdmin";
import PromoCodesPage from "@/pages/agency/PromoCodesPage";
import DomainsPage from "@/pages/agency/DomainsPage";
import CockpitUpdatePage from "@/pages/agency/CockpitUpdatePage";
import {
  AgencyDashboardAdmin, AgencyPricingAdmin, AgencyWidgetCatalogAdmin,
  AgencyCGVAdmin, AgencyClientsAdmin, AgencyOnboardingRequestsAdmin,
} from "@/pages/admin/AgencyAccessAdmin";
import OnboardingRequestPage from "@/pages/public/OnboardingRequestPage";
import OnboardingConfirmationPage from "@/pages/public/OnboardingConfirmationPage";
import {
  CustomerLoginPage, CustomerRegisterPage, AccountLayout,
  AccountDashboard, AccountOrders, AccountOrderDetail, AccountAddresses, AccountProfile, AccountFavorites, AccountWallet,
  OrderConfirmationPage,
} from "@/pages/public/AccountPages";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import JOAdminSidebarInjector from "@/components/jardins-ole/JOAdminSidebarInjector";
import JOHeaderShopShortcut from "@/components/jardins-ole/JOHeaderShopShortcut";
import JOAdminBrandRenamer from "@/components/jardins-ole/JOAdminBrandRenamer";
import JOAdminOrderFulfillmentInjector from "@/components/jardins-ole/JOAdminOrderFulfillmentInjector";
import JOAdminLoginInjector from "@/components/jardins-ole/JOAdminLoginInjector";
import JOProPricingInjector from "@/components/jardins-ole/JOProPricingInjector";
import JORelatedProductsInjector from "@/components/jardins-ole/JORelatedProductsInjector";
import JOAltTextInjector from "@/components/jardins-ole/JOAltTextInjector";
import JOVivaceBadgesInjector from "@/components/jardins-ole/JOProductBadgesInjector";
import JOCategorySeoInjector from "@/components/jardins-ole/JOCategorySeoInjector";
import JOLeadPopup from "@/components/jardins-ole/JOLeadPopup";
import ClientsAdmin from "@/pages/jardins-ole/ClientsAdmin";
import ClientDetailAdmin from "@/pages/jardins-ole/ClientDetailAdmin";
import ChantiersAdmin from "@/pages/jardins-ole/ChantiersAdmin";
import ChantierEditAdmin from "@/pages/jardins-ole/ChantierEditAdmin";
import BillingProAdmin from "@/pages/jardins-ole/BillingProAdmin";
import ClientAnalyticsAdmin from "@/pages/jardins-ole/ClientAnalyticsAdmin";
import DestockageAdmin from "@/pages/jardins-ole/DestockageAdmin";

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <CustomerAuthProvider>
        <CartProvider>
        <BrowserRouter>
          <JOAdminSidebarInjector />
          <JOHeaderShopShortcut />
          <JOAdminBrandRenamer />
          <JOAdminOrderFulfillmentInjector />
          <JOAdminLoginInjector />
          <JOProPricingInjector />
          <JORelatedProductsInjector />
          <JOAltTextInjector />
          <JOVivaceBadgesInjector />
          <JOCategorySeoInjector />
          <JOLeadPopup />
          <Routes>
            {/* PUBLIC */}
            <Route element={<PublicLayout/>}>
              <Route path="/" element={<HomePage/>}/>
              <Route path="/services" element={<ServicesListPage/>}/>
              <Route path="/services/:slug" element={<ServiceDetailPage/>}/>
              <Route path="/realisations" element={<RealisationsListPage/>}/>
              <Route path="/realisations/:slug" element={<RealisationDetailPage/>}/>
              <Route path="/blog" element={<BlogListPage/>}/>
              <Route path="/blog/:slug" element={<BlogDetailPage/>}/>
              <Route path="/zones/:slug" element={<ZonePage/>}/>
              <Route path="/intervention/:slug" element={<LocalPage/>}/>
              <Route path="/contact" element={<ContactPage/>}/>
              <Route path="/equipe" element={<TeamPage/>}/>
              <Route path="/ateliers" element={<WorkshopsListPage/>}/>
              <Route path="/ateliers/:slug" element={<WorkshopDetailPage/>}/>
              <Route path="/devis" element={<DevisPage/>}/>
              <Route path="/shop" element={<ShopPage/>}/>
              <Route path="/shop/:slug" element={<ProductPage/>}/>
              <Route path="/reservation" element={<ReservationPage/>}/>
              <Route path="/booking/confirmation" element={<BookingConfirmationPage/>}/>
              <Route path="/rentals" element={<RentalsListPage/>}/>
              <Route path="/rentals/:slug" element={<RentalDetailPage/>}/>
              <Route path="/rental/confirmation" element={<RentalConfirmationPage/>}/>
              <Route path="/account/login" element={<CustomerLoginPage/>}/>
              <Route path="/account/register" element={<CustomerRegisterPage/>}/>
              <Route path="/account" element={<AccountLayout/>}>
                <Route index element={<AccountDashboard/>}/>
                <Route path="orders" element={<AccountOrders/>}/>
                <Route path="orders/:oid" element={<AccountOrderDetail/>}/>
                <Route path="wallet" element={<AccountWallet/>}/>
                <Route path="favorites" element={<AccountFavorites/>}/>
                <Route path="addresses" element={<AccountAddresses/>}/>
                <Route path="profile" element={<AccountProfile/>}/>
              </Route>
              <Route path="/account/order-confirmation" element={<OrderConfirmationPage/>}/>
              <Route path="/mentions-legales" element={<LegalPage kind="mentions"/>}/>
              <Route path="/cgv" element={<LegalPage kind="cgv"/>}/>
              <Route path="/livraison-retours" element={<DeliveryReturnsPage/>}/>
              <Route path="/confidentialite" element={<LegalPage kind="confidentialite"/>}/>
            </Route>

            {/* Public onboarding — accessible directement sans wrapper (dark theme) */}
            <Route path="/onboarding" element={<OnboardingRequestPage/>}/>
            <Route path="/onboarding/confirmation" element={<OnboardingConfirmationPage/>}/>

            {/* SHORT LINK — public redirect */}
            <Route path="/l/:slug" element={<ShortLinkRedirect/>}/>

            {/* ADMIN */}
            <Route path="/admin/login" element={<LoginPage/>}/>
            <Route path="/admin/sso" element={<PodSsoConsumePage/>}/>
            <Route path="/admin" element={<AdminLayout/>}>
              <Route index element={<Dashboard/>}/>
              <Route path="dashboard" element={<Dashboard/>}/>
              <Route path="services" element={<ServicesAdmin/>}/>
              <Route path="realisations" element={<RealisationsAdmin/>}/>
              <Route path="blog" element={<BlogAdmin/>}/>
              <Route path="faq" element={<FAQAdmin/>}/>
              <Route path="zones" element={<ZonesAdmin/>}/>
              <Route path="leads" element={<LeadsAdmin/>}/>
              <Route path="analytics" element={<AnalyticsAdmin/>}/>
              <Route path="rank-tracker" element={<RankTrackerAdmin/>}/>
              <Route path="local-seo" element={<LocalSEOAdmin/>}/>
              <Route path="media" element={<MediaAdmin/>}/>
              <Route path="short-links" element={<ShortLinksAdmin/>}/>
              <Route path="themes" element={<ThemesAdmin/>}/>
              <Route path="products" element={<ProductsAdmin/>}/>
              <Route path="shop-dashboard" element={<ShopDashboard/>}/>
              <Route path="pickup" element={<PickupAdmin/>}/>
              <Route path="orders" element={<OrdersAdmin/>}/>
              <Route path="qr-labels" element={<QrLabelsAdmin/>}/>
              <Route path="team" element={<TeamAdmin/>}/>
              <Route path="ateliers" element={<WorkshopsAdmin/>}/>
              <Route path="clients" element={<ClientsAdmin/>}/>
              <Route path="clients/:cid" element={<ClientDetailAdmin/>}/>
              <Route path="chantiers" element={<ChantiersAdmin/>}/>
              <Route path="chantiers/:wid" element={<ChantierEditAdmin/>}/>
              <Route path="facturation-pro" element={<BillingProAdmin/>}/>
              <Route path="analyse-clients" element={<ClientAnalyticsAdmin/>}/>
              <Route path="destockage" element={<DestockageAdmin/>}/>
              <Route path="agent-bridge" element={<AgentBridgeAdmin/>}/>
              <Route path="bookings" element={<BookingsAdmin/>}/>
              <Route path="bookings-config" element={<BookingsConfigAdmin/>}/>
              <Route path="rentals" element={<RentalsAdmin/>}/>
              <Route path="rentals-calendar" element={<RentalsCalendarAdmin/>}/>
              <Route path="rentals-dashboard" element={<RentalsDashboardAdmin/>}/>
              <Route path="sector-presets" element={<SectorPresetsAdmin/>}/>
              <Route path="agency-sites" element={<AgencySitesDashboard/>}/>
              <Route path="agency-alerts" element={<AgencyAlertsPage/>}/>
              <Route path="widgets" element={<WidgetsConsoleAdmin/>}/>
              <Route path="technical" element={<TechnicalAdmin/>}/>
              <Route path="legal" element={<LegalConfigAdmin/>}/>
              <Route path="legal-docs" element={<LegalDocsAdmin/>}/>
              <Route path="billing" element={<BillingAdmin/>}/>
              <Route path="shipping" element={<ShippingAdmin/>}/>
              <Route path="business-rules" element={<BusinessRulesAdmin/>}/>
              <Route path="rules/approval" element={<BusinessRulesAdmin section="approval"/>}/>
              <Route path="rules/b2b" element={<BusinessRulesAdmin section="b2b"/>}/>
              <Route path="rules/loyalty" element={<BusinessRulesAdmin section="loyalty"/>}/>
              <Route path="emails" element={<EmailsAdmin/>}/>
              <Route path="popup-callback" element={<PopupCallbackAdmin/>}/>
              <Route path="wishlist" element={<WishlistAdmin/>}/>
              <Route path="payments" element={<PaymentsAdmin/>}/>
              <Route path="onboarding" element={<OnboardingPage/>}/>
              <Route path="cockpit" element={<CockpitAdmin/>}/>
              <Route path="studio" element={<StudioAdmin/>}/>
              <Route path="sections" element={<HomeSectionsToggleAdmin/>}/>
              <Route path="import-products" element={<ProductsImporterAdmin/>}/>
              <Route path="agency-pairing" element={<AgencyPairingPage/>}/>
              <Route path="header" element={<HeaderAdmin/>}/>
              <Route path="settings" element={<SettingsAdmin/>}/>
            </Route>

            {/* ACCÈS AGENCE — layout dédié (dark cockpit), gated by is_hub + role=admin */}
            <Route path="/agency/login" element={<AgencyLoginPage/>}/>
            <Route path="/agency" element={<AgencyLayout/>}>
              <Route index element={<AgencyDashboardAdmin/>}/>
              <Route path="dashboard" element={<AgencyDashboardAdmin/>}/>
              <Route path="sites" element={<AgencySitesDashboard/>}/>
              <Route path="alerts" element={<AgencyAlertsPage/>}/>
              <Route path="clients" element={<AgencyClientsAdmin/>}/>
              <Route path="onboarding" element={<AgencyOnboardingRequestsAdmin/>}/>
              <Route path="pricing" element={<AgencyPricingAdmin/>}/>
              <Route path="widgets" element={<AgencyWidgetCatalogAdmin/>}/>
              <Route path="cgv" element={<AgencyCGVAdmin/>}/>
              <Route path="studio" element={<StudioAdmin/>}/>
              <Route path="theme-generator" element={<ThemeGeneratorAdvanced/>}/>
              <Route path="content-scraper" element={<ContentScraperPage/>}/>
              <Route path="drafts" element={<OnboardingDraftsPage/>}/>
              <Route path="cockpit" element={<AgencyCockpitAdmin/>}/>
              <Route path="promo-codes" element={<PromoCodesPage/>}/>
              <Route path="domains" element={<DomainsPage/>}/>
              <Route path="cockpit-update" element={<CockpitUpdatePage/>}/>
              <Route path="presets" element={<SectorPresetsAdmin/>}/>
              <Route path="settings" element={<AgencySettingsPage/>}/>
            </Route>
          </Routes>
          <Toaster position="top-right" richColors/>
        </BrowserRouter>
        </CartProvider>
        </CustomerAuthProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}
